import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import { FaTruck, FaHourglassHalf, FaIndianRupeeSign } from 'react-icons/fa6';
import { FaCreditCard, FaCheckCircle, FaTimesCircle, FaSearch } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/CheckOrder.css';
import { formatPrice } from '../utils/formatPrice';
import { checkOrderById } from '../api/apiService';

const API_BASE_URL = 'https://vinirji.com'; // Update with your actual backend URL

const CheckOrder = () => {
    const [orderId, setOrderId] = useState('');
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [socket, setSocket] = useState(null);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!orderId.trim()) {
            setError('Please enter an Order ID.');
            return;
        }
        setLoading(true);
        setError('');
        setOrder(null);

        if (socket) {
            socket.disconnect();
        }

        try {
            const response = await checkOrderById(orderId.trim());
            
            if (!response.data.success) {
                throw new Error(response.data.message || 'Failed to fetch order details.');
            }

            const fetchedOrder = response.data.data;
            setOrder(fetchedOrder);

            const newSocket = io(API_BASE_URL);
            newSocket.on('connect', () => {
                newSocket.emit('joinOrderRoom', fetchedOrder._id);
            });

            newSocket.on('orderUpdate', (updatedOrder) => {
                if (updatedOrder._id === fetchedOrder._id) {
                    setOrder(updatedOrder);
                }
            });

            setSocket(newSocket);

        } catch (err) {
            setError(err.response?.data?.message || err.message || 'An unexpected error occurred.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (socket) {
                socket.disconnect();
            }
        };
    }, [socket]);
    
    const getPaymentIcon = (paymentMode) => {
        switch (paymentMode) {
            case 'Stripe':
            case 'Razorpay':
                return <FaCreditCard />;
            default:
                return <FaIndianRupeeSign />;
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'delivered':
                return <FaCheckCircle className="text-green-500" />;
            case 'out_for_delivery':
            case 'ready_to_pickup':
            case 'preparing':
            case 'accepted':
            case 'placed':
                return <FaTruck className="text-yellow-500" />;
            case 'cancelled':
            case 'rejected':
            case 'payment_failed':
                return <FaTimesCircle className="text-red-500" />;
            default:
                return <FaHourglassHalf className="text-gray-500" />;
        }
    };

    const getStatusMessage = (status) => {
        const statusMap = {
            'pending': 'Order is being processed.',
            'pending_payment': 'Waiting for payment confirmation.',
            'placed': 'Order has been placed.',
            'accepted': 'Order has been accepted by the store.',
            'preparing': 'The kitchen is preparing your order.',
            'ready_to_pickup': 'Your order is ready for pickup.',
            'out_for_delivery': 'Your order is on its way!',
            'delivered': 'Your order has been successfully delivered.',
            'cancelled': 'The order was cancelled.',
            'rejected': 'The order was rejected by the store. Contact support for details.',
            'payment_failed': 'Payment for the order failed.',
        };
        return statusMap[status] || 'Status update.';
    };

    const getDeliveredTime = (statusHistory) => {
        const deliveredEvent = statusHistory?.find(event => event.status === 'delivered');
        return deliveredEvent ? deliveredEvent.updatedAt : 'N/A';
    };

    const StatusTimelineItem = ({ event, isLast, isActive }) => (
        <div className="timeline-item">
            <div className="timeline-icon-col">
                <div className={`timeline-icon ${isActive ? 'active' : ''}`}>
                    {getStatusIcon(event.status)}
                </div>
                {!isLast && <div className={`timeline-connector ${isActive ? 'active' : ''}`} />}
            </div>
            <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className={`timeline-content ${isActive ? 'active' : ''}`}
            >
                <div className="timeline-status">
                    {event.status?.replace(/_/g, ' ')}
                </div>
                <div className="timeline-date">{event.updatedAt}</div>
                <div className="timeline-message">{getStatusMessage(event.status)}</div>
            </motion.div>
        </div>
    );

    return (
        <div className="check-order-bg">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="check-order-card"
            >
                <div className="check-order-header">
                    <h1>Check Your Order</h1>
                    <p>Enter your Order ID to see the latest status.</p>
                    <p className="order-id-info">
                        Note: Order details are only available for 24 hours after placement.
                    </p>
                </div>
                <form onSubmit={handleSearch} className="check-order-form">
                    <div className="input-group">
                        {/* Input comes first in DOM, Icon is absolute positioned over it */}
                        <input
                            type="text"
                            value={orderId}
                            onChange={(e) => setOrderId(e.target.value)}
                            placeholder="e.g., V20157713ER"
                            className="order-id-input"
                        />
                        <FaSearch className="search-icon" />
                    </div>
                    <button type="submit" disabled={loading} className="search-btn">
                        {loading ? 'Checking...' : 'Check Order'}
                    </button>
                </form>

                <AnimatePresence mode="wait">
                    {loading && (
                        <motion.div
                            key="loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="loading-section"
                        >
                            <div className="loading-spinner"></div>
                            <span>Loading order details...</span>
                        </motion.div>
                    )}

                    {error && (
                        <motion.div
                            key="error"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="error-section"
                            role="alert"
                        >
                            <span>{error}</span>
                        </motion.div>
                    )}

                    {order && !loading && (
                        <motion.div
                            key="order-details"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="order-details-grid"
                        >
                            {/* Left Column: Order Summary */}
                            <div className="order-summary">
                                <h3>Order Summary</h3>
                                <div className="summary-card">
                                    <div className="summary-row">
                                        <span>Order ID</span>
                                        <span>{order._id}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span>Placed On</span>
                                        <span>{new Date(order.createdAt).toLocaleDateString()}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span>Total Amount</span>
                                        <span>
                                            {formatPrice(order.net_amount, order.currency, order.currencySymbol)}
                                        </span>
                                    </div>
                                    <div className="summary-row">
                                        <span>Payment Mode</span>
                                        <span className="payment-mode">
                                            {getPaymentIcon(order.payment_mode)}
                                            <span>Online Payment</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Right Column: Status Timeline */}
                            <div className="order-timeline">
                                {order.status === 'delivered' ? (
                                    <motion.div
                                        key="delivered-message"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="delivered-final-status"
                                    >
                                        <h3>Order Delivered</h3>
                                        <div className="delivered-content">
                                            <FaCheckCircle className="delivered-icon" />
                                            <p>Your order was delivered at {getDeliveredTime(order.status_history)}.</p>
                                        </div>
                                    </motion.div>
                                ) : order.status === 'rejected' || order.status === 'cancelled' ? (
                                    <motion.div
                                        key="rejected-message"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="rejected-final-status"
                                    >
                                        <h3>Order {order.status?.replace(/_/g, ' ')}</h3>
                                        <div className="rejected-content">
                                            <FaTimesCircle className="rejected-icon" />
                                            <p>{getStatusMessage(order.status)}</p>
                                            <p>For support, contact <a href="mailto:vinirji@support.com">vinirji@support.com</a>.</p>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <>
                                        <h3>Order Progress</h3>
                                        <div className="timeline-list">
                                            {order.status_history?.map((event, index) => (
                                                <StatusTimelineItem
                                                    key={index}
                                                    event={event}
                                                    isLast={index === order.status_history.length - 1}
                                                    isActive={event.status === order.status}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

export default CheckOrder;